import { useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { CONTRACT_ADDRESS, USDT_ADDRESS } from "../config/contract";
import { PACKAGES, MATRIX_LEVELS } from "../config/constants";
import { bytes32ToString, parseUSDT } from "../utils/format";
import { useNotification } from "./useNotification";
import { parseError } from "../utils/errorParser";

// Fetch user's full profile (registration status, packages, income, eligibility)
export function useUserProfile() {
  const { account, getContract, isCorrectChain } = useWeb3();
  const [state, setState] = useState({
    loading: true,
    isRegistered: false,
    user: null,
    packages: [],
    incomeBreakdown: null,
    eligibility: null,
    claimable: null,
    directRefsCountPerPackage: [],
  });
  const [reloadKey, setReloadKey] = useState(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      if (!account || !contract || !isCorrectChain) {
        if (!cancelRef.current) {
          setState((s) => ({ ...s, loading: false, isRegistered: false, user: null }));
        }
        return;
      }
      try {
        setState((s) => ({ ...s, loading: true }));
        const userId = await contract.addressToId(account);
        const uidNum = userId.toNumber ? userId.toNumber() : Number(userId.toString());
        if (uidNum === 0) {
          if (!cancelRef.current) {
            setState({
              loading: false,
              isRegistered: false,
              user: null,
              packages: [],
              incomeBreakdown: null,
              eligibility: null,
              claimable: null,
              directRefsCountPerPackage: [],
            });
          }
          return;
        }

        const rawUser = await contract.users(userId);
        const user = {
          wallet: rawUser.wallet,
          name: bytes32ToString(rawUser.name),
          id: rawUser.id.toString(),
          referrerId: rawUser.referrerId.toString(),
          serialNumber: rawUser.serialNumber.toString(),
          joinTime: rawUser.joinTime.toNumber(),
          isBlocked: rawUser.isBlocked,
          registrationType: rawUser.registrationType,
          totalDirectReferrals: rawUser.totalDirectReferrals.toString(),
          totalClaimed: rawUser.totalClaimed,
          totalIncome: rawUser.totalIncome,
        };

        // Packages — struct now has ceilingTier (uint8) instead of ceilingUpgraded (bool)
        const pkgs = await Promise.all(
          PACKAGES.map(async (p) => {
            const raw = await contract.userPackages(userId, p.id);
            const tier = raw.ceilingTier ?? 3;
            return {
              ...p,
              isActive: raw.isActive,
              ceilingTier: typeof tier === "number" ? tier : Number(tier.toString?.() || tier),
              ceilingUpgraded: (typeof tier === "number" ? tier : Number(tier)) >= 4, // legacy alias
              activationTime: raw.activationTime.toNumber(),
              totalInvested: raw.totalInvested,
              totalEarned: raw.totalEarned,
              ceilingLimit: raw.ceilingLimit,
            };
          })
        );

        // Income breakdown — 3 streams: direct, matrix, royalty (formerly "daily club")
        const rawIncome = await contract.userIncomeBreakdown(userId);
        const incomeBreakdown = {
          directReferralIncome: rawIncome.directReferralIncome,
          matrixLevelIncome: rawIncome.matrixLevelIncome,
          dailyClubIncome: rawIncome.dailyClubIncome,
        };

        // Eligibility — 7 daily royalties.
        // NOTE: ethers v5 unwraps single-return functions to the raw value,
        // so `rawElig` IS the bool[7] array directly, NOT a Result wrapper.
        // (`.royaltyEligible` is undefined; `rawElig[0]` is the first bool, not the array.)
        const rawElig = await contract.getUserEligibility(userId);
        const eligArr = Array.isArray(rawElig)
          ? rawElig
          : (rawElig?.royaltyEligible || [false, false, false, false, false, false, false]);
        const eligibility = { daily: eligArr };

        // Claimable — multi-return function, so `rawClaim` IS a Result wrapper here.
        const rawClaim = await contract.getUserClaimableRaw(userId);
        const rawDaily = rawClaim.royaltyClaimable || rawClaim[0];
        // Clamp each royalty's claimable by the recipient's charge-package remaining ceiling
        const clampedDaily = [];
        let clampedTotal = ethers.BigNumber.from(0);
        for (let c = 1; c <= 7; c++) {
          const raw = rawDaily[c - 1] || ethers.BigNumber.from(0);
          const chargePkg = pkgs[c]; // clubId N → charge package N+1 (index c since packages array is 0-indexed)
          let allowed = ethers.BigNumber.from(0);
          if (chargePkg && chargePkg.isActive && raw.gt(0)) {
            const remaining = chargePkg.ceilingLimit.sub(chargePkg.totalEarned);
            if (remaining.gt(0)) {
              allowed = raw.lt(remaining) ? raw : remaining;
            }
          }
          clampedDaily.push(allowed);
          clampedTotal = clampedTotal.add(allowed);
        }
        const claimable = {
          daily: clampedDaily,
          total: clampedTotal,
        };

        const directs = await Promise.all(
          PACKAGES.map(async (p) => {
            const c = await contract.activeDirectsPerPackage(userId, p.id);
            return c.toNumber ? c.toNumber() : Number(c.toString());
          })
        );

        if (!cancelRef.current) {
          setState({
            loading: false,
            isRegistered: true,
            user,
            packages: pkgs,
            incomeBreakdown,
            eligibility,
            claimable,
            directRefsCountPerPackage: directs,
          });
        }
      } catch (e) {
        if (!cancelRef.current) {
          setState((s) => ({ ...s, loading: false }));
        }
      }
    };
    load();
    return () => {
      cancelRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isCorrectChain, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);
  return { ...state, reload };
}

// Fetch USDT balance and allowance
export function useUSDT() {
  const { account, getUsdt, isCorrectChain } = useWeb3();
  const [balance, setBalance] = useState(ethers.BigNumber.from(0));
  const [allowance, setAllowance] = useState(ethers.BigNumber.from(0));
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const usdt = getUsdt();
      if (!account || !usdt || !isCorrectChain) return;
      try {
        setLoading(true);
        const [bal, allo] = await Promise.all([
          usdt.balanceOf(account),
          usdt.allowance(account, CONTRACT_ADDRESS),
        ]);
        if (!cancelRef.current) {
          setBalance(bal);
          setAllowance(allo);
        }
      } catch {
        // ignore
      } finally {
        if (!cancelRef.current) setLoading(false);
      }
    };
    load();
    return () => {
      cancelRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isCorrectChain, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);
  return { balance, allowance, loading, reload };
}

// Approve USDT
export function useApproveUSDT() {
  const { getUsdt } = useWeb3();
  const notify = useNotification();
  const [approving, setApproving] = useState(false);

  const approve = async (amount) => {
    const usdt = getUsdt();
    if (!usdt) {
      notify.error("Wallet not connected");
      return false;
    }
    try {
      setApproving(true);
      const amt = amount
        ? parseUSDT(amount)
        : ethers.constants.MaxUint256;
      const tx = await usdt.approve(CONTRACT_ADDRESS, amt);
      notify.info("Approval sent, waiting for confirmation...");
      await tx.wait();
      notify.success("USDT approved");
      return true;
    } catch (e) {
      notify.error(parseError(e));
      return false;
    } finally {
      setApproving(false);
    }
  };

  return { approve, approving };
}

// Wrapper for admin actions. The contract itself logs every admin action
// to Storage.adminActivities on-chain — no off-chain logging needed.
// The `action`/`params` opts are ignored (kept for call-site backwards compat).
export function useAdminTx() {
  const notify = useNotification();
  const [pending, setPending] = useState(false);

  const send = async (fn, opts = {}) => {
    const { pendingMsg, successMsg } = opts;
    try {
      setPending(true);
      const tx = await fn();
      notify.info(pendingMsg || "Transaction sent...");
      const receipt = await tx.wait();
      notify.success(successMsg || "Transaction confirmed");
      // Notify any open AdminHistory panels to re-fetch on-chain log
      window.dispatchEvent(new Event("admin-log-updated"));
      return receipt;
    } catch (e) {
      notify.error(parseError(e));
      return null;
    } finally {
      setPending(false);
    }
  };

  return { send, pending };
}

// Send a contract transaction with common error handling.
// The `log` opt is ignored (contract auto-logs).
export function useTx() {
  const notify = useNotification();
  const [pending, setPending] = useState(false);

  const send = async (fn, opts = {}) => {
    const { pendingMsg, successMsg } = opts;
    try {
      setPending(true);
      const tx = await fn();
      notify.info(pendingMsg || "Transaction sent...");
      const receipt = await tx.wait();
      notify.success(successMsg || "Transaction confirmed");
      return receipt;
    } catch (e) {
      notify.error(parseError(e));
      return null;
    } finally {
      setPending(false);
    }
  };

  return { send, pending };
}
